import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useBodyScrollLock } from '../../../hooks/useBodyScrollLock';

/**
 * Vollbild-Player fuer Videos auf den oeffentlichen Seiten (Spot, Webinar).
 *
 * - Natives <dialog> mit showModal(): Fokusfalle, Escape und Top-Layer kommen
 *   vom Browser (wie ui/Modal.jsx). jsdom kennt showModal nicht → open-Attribut.
 * - Das <video> existiert nur, solange der Dialog offen ist: vorher wird kein
 *   Byte geladen, nach dem Schliessen bricht der Download ab.
 * - Hochformat-Telefone bekommen die 9:16-Fassung (falls vorhanden), alle
 *   anderen 16:9. Entschieden wird beim Oeffnen.
 * - Fehlt die Datei auf dem Server, liefert nginx per try_files die index.html
 *   aus — das Video meldet dann einen Fehler und wir sagen das ehrlich.
 * - Fokus geht beim Schliessen an den Ausloeser zurueck.
 *
 * sources: { landscape: { poster, webm?, mp4 }, portrait?: { … } }
 */
// jsdom (Tests) kennt HTMLDialogElement nicht: dort zaehlt das Attribut.
const isDialogOpen = (d) => d.open === true || d.hasAttribute('open');

const pickPortrait = () =>
  typeof window !== 'undefined' && window.matchMedia
    ? window.matchMedia('(orientation: portrait) and (max-width: 767px)').matches
    : false;

const VideoDialog = ({ open, onClose, title, sources, transcript = null }) => {
  const dialogRef = useRef(null);
  const videoRef = useRef(null);
  const returnFocusRef = useRef(null);
  const [portrait, setPortrait] = useState(false);
  const [failed, setFailed] = useState(false);
  useBodyScrollLock(open);

  const src = portrait && sources.portrait ? sources.portrait : sources.landscape;

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !isDialogOpen(dialog)) {
      returnFocusRef.current = document.activeElement;
      setPortrait(pickPortrait());
      setFailed(false);
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
    }
    if (!open && isDialogOpen(dialog)) {
      if (typeof dialog.close === 'function') dialog.close();
      else dialog.removeAttribute('open');
      const back = returnFocusRef.current;
      if (back && typeof back.focus === 'function') back.focus();
    }
  }, [open]);

  // Abspielen, sobald das Video im DOM steht (der Klick war die Nutzergeste).
  useEffect(() => {
    if (!open) return;
    const video = videoRef.current;
    if (!video || typeof video.play !== 'function') return;
    const p = video.play();
    if (p && typeof p.catch === 'function') p.catch(() => { /* abgelehnt: Steuerung bleibt sichtbar */ });
  }, [open, src.mp4]);

  // Escape schliesst den Dialog nativ — State nachziehen.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return undefined;
    const handleCancel = (event) => {
      event.preventDefault();
      onClose();
    };
    dialog.addEventListener('cancel', handleCancel);
    return () => dialog.removeEventListener('cancel', handleCancel);
  }, [onClose]);

  // Der Dialog fuellt den Bildschirm; „daneben klicken“ heisst: auf die dunkle Flaeche.
  const onBackdrop = (event) => {
    if (event.target === event.currentTarget) onClose();
  };

  const wide = 'max-w-[min(1280px,calc(82dvh*16/9))]';

  return createPortal(
    <dialog
      ref={dialogRef}
      aria-label={title}
      className="m-0 p-0 w-screen h-[100dvh] max-w-none max-h-none bg-black/95 backdrop:bg-black/80 text-white overflow-y-auto"
    >
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- Escape schliesst nativ */}
      <div className="min-h-full w-full flex flex-col items-center justify-center gap-4 p-3 pt-16 sm:p-10" onClick={onBackdrop}>
        {open && !failed && (
          <video
            ref={videoRef}
            key={src.mp4}
            className={`${portrait && sources.portrait ? 'h-[78dvh] w-auto aspect-[9/16]' : `w-full ${wide} aspect-video`} rounded-2xl bg-black`}
            controls
            playsInline
            preload="metadata"
            poster={src.poster}
            onError={() => setFailed(true)}
          >
            {src.webm && <source src={src.webm} type="video/webm" />}
            {/* Die letzte Quelle meldet den Fehler, wenn keine spielbar war. */}
            <source src={src.mp4} type="video/mp4" onError={() => setFailed(true)} />
          </video>
        )}
        {open && failed && (
          <div className={`w-full ${wide} aspect-video max-h-[60dvh] rounded-2xl border border-white/15 flex flex-col items-center justify-center gap-2 p-8 text-center`}>
            <p className="text-lg font-semibold">Das Video ist gerade nicht erreichbar.</p>
            <p className="text-sm text-white/70">Versuch es später noch einmal – oder schreib mir an support@eachotter.de.</p>
          </div>
        )}
        {transcript && (
          <details className={`w-full ${wide} text-sm text-white/80`}>
            <summary className="cursor-pointer select-none py-2 font-medium text-white/90 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white">
              Textfassung
            </summary>
            <div className="mt-2 space-y-2 leading-relaxed">{transcript}</div>
          </details>
        )}
      </div>
      <button
        type="button"
        onClick={onClose}
        className="fixed top-3 right-3 sm:top-5 sm:right-5 inline-flex items-center justify-center w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
        style={{ marginTop: 'env(safe-area-inset-top, 0px)' }}
        aria-label="Video schließen"
      >
        <X className="w-5 h-5" aria-hidden="true" />
      </button>
    </dialog>,
    document.body,
  );
};

export default VideoDialog;

/**
 * Der Werbespot (68 s). Dateien: frontend/public/spot/ (Videos nicht im Git, per scp).
 * v3 (Okt 2026): Das Schulnetz zeigt vier Szenarien (Sprachtandem, Software-AG, Aktive
 * Pause, Barcamp), die Musik ist eine Phrase länger, der Otter zeichnet sich schneller.
 * v4 (16. Okt 2026, Aura v2): Die Level-Karte zeigt den Sticker-Otter der App, der Aufstieg
 * geht von „Welle“ zu „Strömung“ (vorher Kostüm-PNGs „Helfer“ → „Mentor“).
 * Neue Dateinamen, damit der Upload vor dem Deploy die Live-Seite nicht berührt.
 */
export const SPOT_SOURCES = {
  landscape: {
    poster: '/spot/eachotter-spot-16x9-v4-poster.jpg',
    webm: '/spot/eachotter-spot-16x9-v4.webm',
    mp4: '/spot/eachotter-spot-16x9-v4.mp4',
  },
  portrait: {
    poster: '/spot/eachotter-spot-9x16-v4-poster.jpg',
    webm: '/spot/eachotter-spot-9x16-v4.webm',
    mp4: '/spot/eachotter-spot-9x16-v4.mp4',
  },
};

/** Textfassung des Spots (Musik ohne Sprache; der Bildtext steht hier). */
export const SPOT_TRANSCRIPT = (
  <>
    <p>Noah sucht jemanden, der ihm GitHub erklärt. Die Suche zeigt Emily aus seiner Schule.</p>
    <p>Finden, fragen, treffen: Noah schreibt Emily, die beiden verabreden eine private Session und checken per QR-Code ein.</p>
    <p>Lea wünscht sich einen Buchclub. Sie legt eine Session an, die offen für alle ist – sieben Leute treten bei.</p>
    <p>Aus vielen solchen Verbindungen wird ein Netz – „Deine Schule hat mehr Expert:innen, als im Stundenplan stehen.“</p>
    <p>Zoe und Finn bringen sich im Sprachtandem Englisch und Deutsch bei. In der Software-AG bauen Emily, Minh, Noah und Zoe eine Vertretungsplan-App. Leon und Kofi spielen in der aktiven Pause Tischtennis. Und beim Barcamp gibt, wer etwas gut erklären kann, eine Session für alle anderen – 38 von 60 Plätzen sind belegt.</p>
    <p>„Alle können was. Und alle brauchen was.“</p>
    <p>Nach jeder Session steht ein Eintrag im Lernlogbuch, Ziele werden abgehakt, und jede gemeinsame Session lässt die Aura wachsen.</p>
    <p>„Was kannst du?“ – Let&apos;s help each otter! eachotter.de – im App Store und für Android.</p>
  </>
);
